import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Sliders = () => {
    const [sliders, setSliders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        button_text: '',
        button_link: '',
        is_active: true,
        image_path: '', // Store the selected image path
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [existingImages, setExistingImages] = useState([]);
    const [useExistingImage, setUseExistingImage] = useState(false);

    // List of images in the slider folder
    const AVAILABLE_IMAGES = [
        { name: 'hero-1.jpeg', path: '/images/slider/hero-1.jpeg' },
        { name: 'hero-2.jpeg', path: '/images/slider/hero-2.jpeg' },
        { name: 'hero-3.jpeg', path: '/images/slider/hero-3.jpeg' },
        { name: 'hero-4.jpeg', path: '/images/slider/hero-4.jpeg' },
        { name: 'slide-1.jpeg', path: '/images/slider/slide-1.jpeg' },
        { name: 'slide-2.jpeg', path: '/images/slider/slide-2.jpeg' },
        { name: 'slide-3.jpeg', path: '/images/slider/slide-3.jpeg' },
        // Add more images as needed
    ];

    useEffect(() => {
        fetchSliders();
        // Check what images exist in the folder
        checkExistingImages();
    }, []);

    const checkExistingImages = () => {
        // This will check which images are available
        // You can also fetch this from the server if needed
        const available = AVAILABLE_IMAGES.filter(img => {
            // Check if image exists by trying to load it
            const imgCheck = new Image();
            imgCheck.src = img.path;
            return true; // We'll assume they exist
        });
        setExistingImages(available);
    };

    const fetchSliders = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('admin_token');
            
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get('https://pitac.gov.pk/api/admin/sliders', {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                }
            });
            
            console.log('Fetched sliders:', response.data);
            setSliders(response.data || []);
            setError('');
        } catch (error) {
            console.error('Error fetching sliders:', error);
            if (error.response?.status === 401) {
                setError('Session expired. Please login again.');
                localStorage.removeItem('admin_token');
                window.location.href = '/admin/login';
            } else {
                setError('Failed to load sliders: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            const token = localStorage.getItem('admin_token');
            
            if (!formData.title.trim()) {
                setError('Title is required');
                setSubmitting(false);
                return;
            }

            // Prepare data for API
            let updateData = {
                title: formData.title,
                subtitle: formData.subtitle || '',
                button_text: formData.button_text || '',
                button_link: formData.button_link || '',
                is_active: formData.is_active ? 1 : 0,
            };

            // If using existing image, add it to the data
            if (useExistingImage && formData.image_path) {
                updateData.image_path = formData.image_path;
            }

            console.log('Saving slider data:', updateData);

            let response;
            
            if (editing) {
                // If we have an image file to upload
                if (imageFile && !useExistingImage) {
                    const formDataToSend = new FormData();
                    Object.keys(updateData).forEach(key => {
                        formDataToSend.append(key, updateData[key]);
                    });
                    formDataToSend.append('image', imageFile);

                    response = await axios.post(
                        `https://pitac.gov.pk/api/admin/sliders/${editing.id}?_method=PUT`,
                        formDataToSend,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data',
                                'Accept': 'application/json',
                            }
                        }
                    );
                } else {
                    // Use JSON for update (with existing image)
                    response = await axios.put(
                        `https://pitac.gov.pk/api/admin/sliders/${editing.id}`,
                        updateData,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                                'Accept': 'application/json',
                            }
                        }
                    );
                }
                console.log('Update response:', response.data);
            } else {
                // Create new slider
                if (imageFile && !useExistingImage) {
                    // Upload new image
                    const formDataToSend = new FormData();
                    Object.keys(updateData).forEach(key => {
                        formDataToSend.append(key, updateData[key]);
                    });
                    formDataToSend.append('image', imageFile);

                    response = await axios.post(
                        'https://pitac.gov.pk/api/admin/sliders',
                        formDataToSend,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data',
                                'Accept': 'application/json',
                            }
                        }
                    );
                } else {
                    // Use existing image from folder
                    response = await axios.post(
                        'https://pitac.gov.pk/api/admin/sliders',
                        updateData,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                                'Accept': 'application/json',
                            }
                        }
                    );
                }
                console.log('Create response:', response.data);
            }

            // Force refresh the list
            await fetchSliders();
            resetForm();
            alert(editing ? 'Slider updated successfully!' : 'Slider created successfully!');
            
        } catch (error) {
            console.error('Error saving slider:', error);
            
            if (error.response) {
                console.log('Error response:', error.response.data);
                if (error.response.status === 422) {
                    const errors = error.response.data.errors || {};
                    const messages = Object.values(errors).flat().join(', ');
                    setError(`Validation error: ${messages}`);
                } else {
                    setError(error.response.data?.message || `Error: ${error.response.status}`);
                }
            } else if (error.request) {
                setError('No response from server. Make sure the backend is running.');
            } else {
                setError(error.message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this slider?')) {
            try {
                const token = localStorage.getItem('admin_token');
                await axios.delete(`https://pitac.gov.pk/api/admin/sliders/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                await fetchSliders();
                alert('Slider deleted successfully!');
            } catch (error) {
                console.error('Error deleting slider:', error);
                alert('Failed to delete slider');
            }
        }
    };

    const handleEdit = (slider) => {
        console.log('Editing slider:', slider);
        setEditing(slider);
        setFormData({
            title: slider.title || '',
            subtitle: slider.subtitle || '',
            button_text: slider.button_text || '',
            button_link: slider.button_link || '',
            is_active: slider.is_active === 1 || slider.is_active === true,
            image_path: slider.image_path || '',
        });
        setImagePreview(slider.image_path);
        setImageFile(null);
        setError('');
        setShowModal(true);
        setUseExistingImage(!!slider.image_path && !slider.image_path.startsWith('/storage/'));
    };

    const resetForm = () => {
        setFormData({
            title: '',
            subtitle: '',
            button_text: '',
            button_link: '',
            is_active: true,
            image_path: '',
        });
        setImageFile(null);
        setImagePreview(null);
        setEditing(null);
        setShowModal(false);
        setError('');
        setSubmitting(false);
        setUseExistingImage(false);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setUseExistingImage(false);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleExistingImageSelect = (imagePath) => {
        setFormData({...formData, image_path: imagePath});
        setImagePreview(imagePath);
        setUseExistingImage(true);
        setImageFile(null);
    };

    if (loading) return <div className="admin-loading">Loading sliders...</div>;

    return (
        <div>
            <div className="admin-header">
                <h2>Sliders Management ({sliders.length} sliders)</h2>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button 
                        className="admin-add-btn" 
                        onClick={fetchSliders}
                        style={{ background: '#6b7280' }}
                    >
                        🔄 Refresh
                    </button>
                    <button className="admin-add-btn" onClick={() => setShowModal(true)}>
                        + Add New Slider
                    </button>
                </div>
            </div>

            {error && (
                <div style={{ 
                    background: '#fef2f2', 
                    color: '#dc2626', 
                    padding: '0.8rem 1rem', 
                    borderRadius: '8px', 
                    marginBottom: '1rem',
                    border: '1px solid #fecaca'
                }}>
                    <strong>Error:</strong> {error}
                    <button 
                        onClick={fetchSliders}
                        style={{ 
                            marginLeft: '1rem', 
                            padding: '0.3rem 1rem',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}

            {sliders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7a8f' }}>
                    <p>No sliders found. Click "Add New Slider" to create one.</p>
                </div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Title</th>
                                <th>Subtitle</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sliders.map((slider) => (
                                <tr key={slider.id}>
                                    <td>
                                        {slider.image_path ? (
                                            <img 
                                                src={slider.image_path && slider.image_path.startsWith('/storage/') 
                                                    ? `https://pitac.gov.pk${slider.image_path}` 
                                                    : slider.image_path}
                                                alt={slider.title}
                                                className="admin-image-preview-thumb"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = '<span style="color:#9ca3af">No image</span>';
                                                }}
                                            />
                                        ) : (
                                            <span style={{ color: '#9ca3af' }}>No image</span>
                                        )}
                                    </td>
                                    <td><strong>{slider.title}</strong></td>
                                    <td>{slider.subtitle || '-'}</td>
                                    <td>
                                        <span className={`admin-status-badge ${slider.is_active ? 'admin-status-active' : 'admin-status-inactive'}`}>
                                            {slider.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="admin-actions">
                                            <button className="admin-edit-btn" onClick={() => handleEdit(slider)}>Edit</button>
                                            <button className="admin-delete-btn" onClick={() => handleDelete(slider.id)}>Delete</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="admin-modal-overlay" onClick={() => resetForm()}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2>{editing ? 'Edit Slider' : 'Add New Slider'}</h2>
                            <button className="admin-modal-close" onClick={resetForm}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            {error && (
                                <div style={{ 
                                    background: '#fef2f2', 
                                    color: '#dc2626', 
                                    padding: '0.8rem', 
                                    borderRadius: '8px', 
                                    marginBottom: '1rem',
                                    border: '1px solid #fecaca'
                                }}>
                                    {error}
                                </div>
                            )}
                            
                            <div className="admin-form-group">
                                <label>Title *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    required
                                    placeholder="Enter slider title"
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Subtitle</label>
                                <input
                                    type="text"
                                    value={formData.subtitle}
                                    onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                                    placeholder="Brief description"
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Button Text</label>
                                <input
                                    type="text"
                                    value={formData.button_text}
                                    onChange={(e) => setFormData({...formData, button_text: e.target.value})}
                                    placeholder="Apply Now"
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Button Link</label>
                                <input
                                    type="text"
                                    value={formData.button_link}
                                    onChange={(e) => setFormData({...formData, button_link: e.target.value})}
                                    placeholder="/courses"
                                />
                            </div>

                            {/* Image Selection Options */}
                            <div className="admin-form-group">
                                <label>Image Source</label>
                                <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                            type="radio"
                                            checked={!useExistingImage}
                                            onChange={() => {
                                                setUseExistingImage(false);
                                                setFormData({...formData, image_path: ''});
                                                setImagePreview(null);
                                            }}
                                        />
                                        Upload New Image
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <input
                                            type="radio"
                                            checked={useExistingImage}
                                            onChange={() => setUseExistingImage(true)}
                                        />
                                        Use Existing Image
                                    </label>
                                </div>
                            </div>

                            {useExistingImage ? (
                                // Show existing images
                                <div className="admin-form-group">
                                    <label>Select Existing Image</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginTop: '0.5rem' }}>
                                        {AVAILABLE_IMAGES.map((img) => (
                                            <div 
                                                key={img.path}
                                                onClick={() => handleExistingImageSelect(img.path)}
                                                style={{
                                                    border: formData.image_path === img.path ? '3px solid #124D1C' : '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    padding: '0.5rem',
                                                    cursor: 'pointer',
                                                    textAlign: 'center',
                                                    background: formData.image_path === img.path ? '#f0f7f0' : 'white'
                                                }}
                                            >
                                                <img 
                                                    src={img.path} 
                                                    alt={img.name}
                                                    style={{ 
                                                        width: '100%', 
                                                        height: '60px', 
                                                        objectFit: 'cover',
                                                        borderRadius: '4px'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.parentElement.innerHTML = '<span style="color:#9ca3af;font-size:0.7rem;">No image</span>';
                                                    }}
                                                />
                                                <div style={{ fontSize: '0.6rem', marginTop: '0.3rem', color: '#6b7a8f' }}>
                                                    {img.name}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {formData.image_path && (
                                        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#16a34a' }}>
                                            ✅ Selected: {formData.image_path}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // Upload new image
                                <div className="admin-form-group">
                                    <label>Upload New Image</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                    {imagePreview && (
                                        <div style={{ marginTop: '10px' }}>
                                            <img 
                                                src={imagePreview} 
                                                alt="Preview" 
                                                className="admin-image-preview"
                                                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {editing && !imageFile && imagePreview && !useExistingImage && (
                                <div style={{ marginTop: '5px', fontSize: '0.8rem', color: '#6b7a8f' }}>
                                    Current image shown. Upload new to replace.
                                </div>
                            )}

                            <div className="admin-form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                    />
                                    Active
                                </label>
                            </div>
                            <button 
                                type="submit" 
                                className="admin-submit-btn"
                                disabled={submitting}
                            >
                                {submitting ? 'Saving...' : (editing ? 'Update Slider' : 'Create Slider')}
                            </button>
                            <button 
                                type="button" 
                                onClick={resetForm}
                                style={{ 
                                    marginTop: '0.5rem',
                                    padding: '0.5rem',
                                    background: 'transparent',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    width: '100%',
                                    color: '#6b7a8f'
                                }}
                            >
                                Cancel
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sliders;